import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER_GROUPS = [
  { group: 'Customer', items: [
    { code: '{{name}}', description: 'Customer full name' },
    { code: '{{first_name}}', description: 'First name' },
    { code: '{{last_name}}', description: 'Last name' },
    { code: '{{salutation}}', description: 'Salutation (Mr./Mrs.)' },
    { code: '{{phone}}', description: 'Mobile number' },
    { code: '{{whatsapp}}', description: 'WhatsApp number' },
    { code: '{{email}}', description: 'Email address' },
    { code: '{{city}}', description: 'City' },
    { code: '{{address}}', description: 'Full address' },
  ]},
  { group: 'Vehicle Interest', items: [
    { code: '{{brand}}', description: 'Interested brand' },
    { code: '{{model}}', description: 'Brand + Model' },
    { code: '{{variant}}', description: 'Variant' },
    { code: '{{color}}', description: 'Interested color' },
    { code: '{{budget_min}}', description: 'Budget minimum' },
    { code: '{{budget_max}}', description: 'Budget maximum' },
  ]},
  { group: 'Lead Details', items: [
    { code: '{{lead_no}}', description: 'Lead number' },
    { code: '{{source}}', description: 'Lead source' },
    { code: '{{sub_source}}', description: 'Sub source' },
    { code: '{{stage}}', description: 'Current stage' },
    { code: '{{priority}}', description: 'Priority (Hot/Warm/Cold)' },
    { code: '{{remarks}}', description: 'Remarks' },
    { code: '{{created_date}}', description: 'Lead created date' },
  ]},
  { group: 'Sales Rep', items: [
    { code: '{{sales_rep}}', description: 'Assigned sales rep' },
    { code: '{{sales_rep_phone}}', description: 'Sales rep phone' },
    { code: '{{sales_rep_email}}', description: 'Sales rep email' },
  ]},
  { group: 'Follow-up', items: [
    { code: '{{follow_up_date}}', description: 'Next follow-up date' },
    { code: '{{follow_up_time}}', description: 'Next follow-up time' },
    { code: '{{last_activity}}', description: 'Last activity description' },
  ]},
  { group: 'Finance', items: [
    { code: '{{financier_name}}', description: 'Preferred financier' },
    { code: '{{finance_status}}', description: 'Finance status' },
    { code: '{{loan_amount}}', description: 'Loan amount' },
  ]},
  { group: 'Company', items: [
    { code: '{{company_name}}', description: 'Dealership name' },
    { code: '{{company_phone}}', description: 'Dealership phone' },
    { code: '{{company_address}}', description: 'Dealership address' },
  ]},
];

function TemplateManagement({ user, onLogout }) {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('email');
  const [showModal, setShowModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState(null);
  const [formData, setFormData] = React.useState({
    name: '',
    type: 'email',
    subject: '',
    body: '',
    description: ''
  });

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  };

  const openCreateModal = (type) => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      type: type,
      subject: '',
      body: '',
      description: ''
    });
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject || '',
      body: template.body,
      description: template.description || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.body) {
      toast.error('Name and Body are required');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${BACKEND_URL}/api/templates/${editingTemplate.id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/templates`, formData);
        toast.success('Template created successfully');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const insertPlaceholder = (placeholder) => {
    setFormData(prev => ({
      ...prev,
      body: prev.body + placeholder
    }));
  };

  const filteredTemplates = templates.filter(t => t.type === activeTab);

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Template Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Template Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Message Templates</h2>
            <p className="text-sm text-gray-500">Create and manage Email & WhatsApp message templates</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'email'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✉️ Email Templates ({templates.filter(t => t.type === 'email').length})
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'whatsapp'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            💬 WhatsApp Templates ({templates.filter(t => t.type === 'whatsapp').length})
          </button>
        </div>

        {/* Add Button */}
        <div className="flex justify-end">
          <button
            onClick={() => openCreateModal(activeTab)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            + Add {activeTab === 'email' ? 'Email' : 'WhatsApp'} Template
          </button>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">No {activeTab} templates yet</p>
            <p className="text-sm">Click "Add Template" to create your first template</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">{template.name}</h3>
                    {template.description && (
                      <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {template.type === 'email' && template.subject && (
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">Subject:</span>
                    <p className="text-sm text-gray-700 truncate">{template.subject}</p>
                  </div>
                )}

                <div className="mb-4">
                  <span className="text-xs text-gray-500">Body Preview:</span>
                  <div 
                    className="text-sm text-gray-600 mt-1 max-h-24 overflow-hidden"
                    style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {template.body.replace(/<[^>]*>/g, ' ').substring(0, 150)}...
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(template)}
                    className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Placeholders Help */}
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3">📝 Available Placeholders</h3>
          <p className="text-sm text-blue-600 mb-3">
            Use these placeholders in your templates. They will be automatically replaced with lead data.
          </p>
          <div className="space-y-4">
            {PLACEHOLDER_GROUPS.map(grp => (
              <div key={grp.group}>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{grp.group}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {grp.items.map(p => (
                    <div key={p.code} className="bg-white rounded-lg px-3 py-2 border border-blue-100 cursor-pointer hover:bg-blue-50" onClick={() => navigator.clipboard.writeText(p.code)}>
                      <code className="text-sm text-blue-700 font-mono">{p.code}</code>
                      <p className="text-xs text-gray-500">{p.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-4">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., Follow Up, Test Drive Invite"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Brief description of when to use this template"
                  />
                </div>

                {formData.type === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Following up on your interest in {{model}}"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message Body *</label>
                  <div className="mb-2 flex flex-wrap gap-1">
                    {PLACEHOLDER_GROUPS.flatMap(g => g.items).map(p => (
                      <button
                        key={p.code}
                        onClick={() => insertPlaceholder(p.code)}
                        className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                        title={p.description}
                      >
                        {p.code}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    rows={10}
                    placeholder={formData.type === 'email' 
                      ? '<p>Dear {{name}},</p><p>Your message here...</p>'
                      : 'Hello {{name}},\n\nYour message here...'
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.type === 'email' 
                      ? 'You can use HTML tags for formatting (e.g., <p>, <strong>, <ul>, <li>)'
                      : 'Use line breaks for formatting. Emojis are supported.'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LeadsLayout>
  );
}

export default TemplateManagement;
